import { Container } from '@mui/material';
import { useCallback, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link, useSearchParams } from 'react-router-dom';
import Adsense from '../Ads/Adsense';
import { Card } from '../Card/Card';
import { RetirementForm } from './Form/RetirementForm';
import {
  calculateRetirement,
  formatNumber,
  RetirementOptions,
  RetirementResult,
} from './RetirementPage.hooks';
import styles from './RetirementPage.module.css';

function RetirementPage() {
  const [searchParams] = useSearchParams();
  const [result, setResult] = useState<RetirementResult>();
  const resultElementRef = useRef<HTMLDivElement>(null);

  const initialFromQuery = useMemo(() => {
    const asset = searchParams.get('asset');
    return asset ? { totalAsset: Number(asset) } : undefined;
  }, [searchParams]);

  const handleSubmit = useCallback((options: RetirementOptions) => {
    const calculatedResult = calculateRetirement(options);
    setResult(calculatedResult);

    setTimeout(() => {
      resultElementRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 0);
  }, []);

  return (
    <div className={styles.root}>
      <Helmet>
        <title>노후 자금 계산기 - 은퇴 후 자산 시뮬레이션</title>
        <meta
          name="description"
          content="은퇴 자금으로 몇 살까지 생활할 수 있을지 시뮬레이션하세요. 월 생활비와 예상 수익률을 입력하여 자산 고갈 시점을 확인합니다."
        />
      </Helmet>
      <Container maxWidth="sm">
        <Card className={styles.formCard}>
          <div className={styles.header}>
            <h1 className={styles.title}>노후 자금 계산기</h1>
            <p className={styles.description}>은퇴 후 자금으로 몇 살까지 생활할 수 있을까요?</p>
            {!initialFromQuery && (
              <p className={styles.linkHint}>
                <Link to="/compound">복리 계산기</Link>에서 먼저 목표 자산을 계산해보세요.
              </p>
            )}
          </div>
          <RetirementForm
            className={styles.form}
            initialValues={initialFromQuery}
            onSubmit={handleSubmit}
          />
        </Card>
      </Container>
      <Adsense className={styles.adsense} adClient="ca-pub-9359050149154028" adSlot="3275566807" />

      {result && (
        <div ref={resultElementRef}>
          <Container maxWidth="xl">
            <Card className={styles.resultCard}>
              <h2 className={styles.resultCardTitle}>
                시뮬레이션 결과
                {result.isPerpetual ? (
                  <span className={styles.perpetualBadge}>영구 보존 가능</span>
                ) : (
                  <span className={styles.depletionBadge}>{result.depletionAge}세에 자산 소진</span>
                )}
              </h2>
              <div className={styles.summaryGrid}>
                <div className={styles.summaryItem}>
                  <span className={styles.summaryLabel}>월 이자 수익</span>
                  <span className={styles.summaryValue}>
                    {formatNumber(result.monthlyInterestIncome)}
                  </span>
                </div>
                <div className={styles.summaryItem}>
                  <span className={styles.summaryLabel}>유지 가능 기간</span>
                  <span className={styles.summaryValue}>
                    {result.isPerpetual
                      ? '무제한'
                      : `${Math.floor(result.depletionMonth / 12)}년 ${result.depletionMonth % 12}개월`}
                  </span>
                </div>
                {!result.isPerpetual && (
                  <div className={styles.summaryItem}>
                    <span className={styles.summaryLabel}>영구 보존 필요 자산</span>
                    <span className={styles.summaryValue}>
                      {formatNumber(result.requiredAssetForPerpetual)}
                    </span>
                  </div>
                )}
              </div>
            </Card>
          </Container>
        </div>
      )}
    </div>
  );
}

export default RetirementPage;
